import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import { CircularProgress } from '@mui/material';
import { useForm } from 'react-hook-form';

import { PartialMatchingServiceDto } from 'src/generated/services/TFinancialApi';
import Modal from 'src/presentations/components/molecules/modal';
import { useActiveNoContractDraft, useContractChargesDetailsForShippingService, useMatchingServicesForNoContract } from 'src/use-cases/invoice/useNoContract';
import useToast from 'src/use-cases/toast/useToast';
import { FormContainer } from '../../forms/FormContainer';
import { NoContractMain } from './Main';
import { IMatchingServiceData, NoContractForm, RejectEnum, SelectedShippingServiceItem } from './types';
import { useNoRateCardSelector } from './Selector';
import { useInvoiceContext } from '../InvoiceContextProvider';
import { useShipmentSelector } from '../ShipmentSelector';
import { checkShouldFetchDraft } from '../../../../../utils/invoiceMapper';

const StyledModal = styled(Modal)({
  '& .MuiDialog-paper': {
    borderRadius: '1.25rem', // Add border radius here
    width: '80%',
    maxWidth: 'none',
  },
  '& .MuiDialogContent-root': {
    padding: '24px 0 0',
  },
});

const getSelectedWithValidityPeriod = (data: PartialMatchingServiceDto): SelectedShippingServiceItem => ({
  mismatchedFields: data?.mismatchedFields,
  contractInfos: data?.contractInfos,
  existingShippingService: {
    ...data?.existingShippingService,
    // validityPeriod will be set from selected contractInfo
  },
});

export const NoRateCardModal = () => {
  const { invoiceSummary } = useInvoiceContext();
  const { shipment } = useShipmentSelector();
  const { closeNoRateCard, isShowModal } = useNoRateCardSelector();

  const errorOpened = useRef<boolean>(false);
  const [contractFlowId] = useState<string | null>(null);
  const [draftContractFlowId, setDraftContractFlowId] = useState<string | null>(null);
  const openToast = useToast();

  const {
    data: matchingService,
    isLoading: isMatchingServiceLoading,
    isFetched: isServiveFetched,
  } = useMatchingServicesForNoContract(shipment?.shipmentInvoiceId);
  const shouldFetchDraft = checkShouldFetchDraft(shipment?.currentDeviationWorkflow);
  const { data: draft, isLoading: isDraftLoading, isFetched: isDraftFetched } = useActiveNoContractDraft(shouldFetchDraft ? shipment?.shipmentInvoiceId : null);
  const {
    isLoading: isLoadingCharges,
    data: dataCharges,
    isFetched: isChargesFetched,
  } = useContractChargesDetailsForShippingService(draftContractFlowId || null);

  const isLoading = isMatchingServiceLoading || isDraftLoading || (isDraftFetched && draft && draftContractFlowId && isLoadingCharges);

  // Set contractFlowId from draft when available
  useEffect(() => {
    const contractFlowId = draft?.data?.selectedPartialMatchingService?.contractInfos?.[0]?.contractFlowId || null;
    setDraftContractFlowId(contractFlowId);
  }, [draft]);

  const matchingServiceData: IMatchingServiceData | false = useMemo(() => {
    if (isServiveFetched) {
      if (!matchingService?.matchedService || !matchingService?.unmatchedService || !matchingService?.partialMatchingServices) {
        return false;
      }
      const newData = {
        matchedService: matchingService?.matchedService || null,
        unmatchedService: matchingService?.unmatchedService,
        partialMatchingServices: matchingService?.partialMatchingServices?.map((item) => getSelectedWithValidityPeriod(item)),
      };
      return newData;
    }
    return null;
  }, [isServiveFetched, matchingService?.matchedService, matchingService?.partialMatchingServices, matchingService?.unmatchedService]);

  const defaultValues = useMemo(() => {
    const isFetched = !!draftContractFlowId && draft ? isDraftFetched && isChargesFetched : isDraftFetched;

    if (matchingServiceData && isFetched) {
      let selectedPartial = null;
      let newService = null;
      const isReject = draft?.data?.selectedResolutionOption === RejectEnum.REJECT;

      if (draft?.data?.selectedPartialMatchingService && matchingServiceData?.partialMatchingServices && !isReject) {
        newService = getSelectedWithValidityPeriod(draft?.data?.selectedPartialMatchingService);
        const draftContractId = draft?.data?.selectedPartialMatchingService?.contractInfos?.[0]?.contractFlowId;
        selectedPartial = matchingServiceData?.partialMatchingServices.find((op) => op.contractInfos?.[0]?.contractFlowId === draftContractId);
      }

      let newVerdict = '';
      if (draft?.data?.selectedResolutionOption === 'setup-new') {
        newVerdict = 'setup-new';
      } else if (draft?.data?.selectedResolutionOption === RejectEnum.REJECT) {
        newVerdict = 'reject';
      } else if (draft?.data?.selectedPartialMatchingService) {
        newVerdict = 'accept';
      }

      const contractCharges = draft ? draft?.data?.contractCharges : dataCharges;

      const selectedContractInfo = draft?.data?.selectedPartialMatchingService?.contractInfos?.[0];
      const values = {
        verdict: newVerdict,
        agreementType: draft?.data?.selectedResolutionOption || '',
        reason: draft?.data?.rejectedReason || '',
        contractChoice: selectedContractInfo?.contractFlowId || null,
        selectedPartialMatchingService: selectedPartial,
        selectedContractInfo,
        newMatchingService: newService,
        containerCharges: contractCharges?.containerCharges || [],
        declarationCharges: contractCharges?.declarationCharges || [],
        shipmentDeclarationCharges: contractCharges?.shipmentCharges || [],
        weightCharges: contractCharges?.weightCharges || [],
        percentageCharges: contractCharges?.percentageCharges || [],
      };

      return values;
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, matchingServiceData, isDraftFetched, isChargesFetched]);

  const formContext = useForm({
    defaultValues: defaultValues as NoContractForm,
  });

  const onClose = useCallback(() => {
    closeNoRateCard();
  }, [closeNoRateCard]);

  useEffect(() => {
    formContext.reset(defaultValues as NoContractForm);
  }, [defaultValues, formContext]);

  useEffect(() => {
    if (matchingServiceData === false) {
      onClose();

      if (!errorOpened.current) {
        errorOpened.current = true;

        openToast({
          toastType: 'error',
          title: 'Error',
          description: 'No data available to read',
          toastOptions: {
            onClose: () => {
              errorOpened.current = false;
            },
          },
        });
      }
    }
  }, [matchingServiceData, onClose, openToast]);

  return (
    <StyledModal open={isShowModal} onClose={onClose}>
      {isShowModal && (
        <FormContainer formContext={formContext}>
          {isLoading ? (
            <div className='p-48 flex items-center justify-center'>
              <CircularProgress color='primary' />
            </div>
          ) : (
            <>
              {matchingServiceData && defaultValues && (
                <NoContractMain
                  matchingService={matchingServiceData}
                  draft={draft}
                  invoiceSummary={invoiceSummary}
                  onClose={onClose}
                  defaultContractFlowId={contractFlowId}
                />
              )}
            </>
          )}
        </FormContainer>
      )}
    </StyledModal>
  );
};
